/**
 * Maps normalized `CopilotChartData` onto Alvie `ChartCard` props.
 *
 * Flow tools emit charts shaped for the Flow UI. This adapter keeps Alvie
 * ChartCard rendering working while both surfaces exist in parallel.
 *
 * TODO: Long term, update agent tool args/results to match Alvie ChartCard
 * directly and delete this adapter.
 */

import type {
  ChartCardCartesianChartProps,
  ChartCardProps,
  ChartCardScatterChartProps,
  ChartDataRow,
  CartesianChartType
} from 'shared/components/ChartCard/types'
import { type CopilotChartData } from './types'
import {
  buildAgentDimension,
  buildAgentMetric,
  buildForecastSeriesDimension,
  COPILOT_FORECAST_SERIES_FIELD,
  COPILOT_SCATTER_SERIES_FIELD,
  COPILOT_SCATTER_SERIES_VALUE,
  formatCompactDateLabel,
  getEffectiveColorField,
  humanizeFieldLabel,
  isTemporalField,
  normalizeAgentChartRows
} from './inferDataDefinition'

const SUPPORTED_CARTESIAN_CHART_TYPES = new Set<CartesianChartType>(['bar', 'line', 'area'])

const sharedCustomisations = {
  allowChangeCardType: false,
  allowExport: true,
  chartSize: {
    height: 280
  }
} as const

/** Forecast is rendered as a line chart in ChartCard. */
const resolveCartesianChartType = (chartType: CopilotChartData['chartType']): CartesianChartType =>
  chartType === 'forecast' ? 'line' : (chartType as CartesianChartType)

/**
 * Whether this chart has enough structure for ChartCard (non-empty axes and a
 * supported chart type: bar/line/area/scatter/forecast).
 */
export const canMapCopilotChartToChartCard = (chart: CopilotChartData): boolean => {
  if (chart.xField.trim().length === 0 || chart.yField.trim().length === 0) {
    return false
  }

  if (chart.chartType === 'scatter' || chart.chartType === 'forecast') {
    return true
  }

  return SUPPORTED_CARTESIAN_CHART_TYPES.has(chart.chartType as CartesianChartType)
}

/**
 * Builds a ChartCard categorical "values" first axis for bar charts that should
 * stay ordered by category/value rather than as a continuous domain.
 */
const buildCategoricalValuesAxis = (
  xField: string,
  sort?: 'desc'
): ChartCardCartesianChartProps<string, ChartDataRow<string>>['firstAxis'] => ({
  id: 'copilot-dimension',
  type: 'values',
  dimension: xField,
  change: {
    change: 'static',
    options: 'all'
  },
  ...(sort != null ? { sort } : {})
})

/**
 * Chooses ChartCard `firstAxis` encoding from Flow `sortBy` + whether X is temporal.
 * Multi-series bars with `sortBy: data` use a values axis; `sortBy: value` adds desc sort.
 */
const buildFirstAxis = (
  chart: CopilotChartData,
  data: Array<ChartDataRow<string>>,
  hasColorSeries: boolean
): ChartCardCartesianChartProps<string, ChartDataRow<string>>['firstAxis'] => {
  const { xField, chartType, sortBy } = chart
  const isCategoricalBarDataSort = chartType === 'bar' && sortBy === 'data' && !isTemporalField(xField, data)

  if (hasColorSeries) {
    return isCategoricalBarDataSort ? buildCategoricalValuesAxis(xField) : xField
  }

  if (chartType === 'bar' && sortBy === 'value') {
    return buildCategoricalValuesAxis(xField, 'desc')
  }

  if (isCategoricalBarDataSort) {
    return buildCategoricalValuesAxis(xField)
  }

  return xField
}

/** Uses the tool title when present; otherwise builds "Y by X" (+ series / date markers). */
const buildChartTitle = (chart: CopilotChartData, effectiveColorField: string): string => {
  const { title, xField, yField, interventionDate, forecastCutoffDate } = chart
  let resolvedTitle = title.trim()

  if (resolvedTitle.length === 0) {
    if (effectiveColorField.length > 0) {
      resolvedTitle = `${humanizeFieldLabel(yField)} by ${humanizeFieldLabel(xField)} and ${humanizeFieldLabel(
        effectiveColorField
      )}`
    } else {
      resolvedTitle = `${humanizeFieldLabel(yField)} by ${humanizeFieldLabel(xField)}`
    }
  }

  const referenceDate = interventionDate ?? forecastCutoffDate
  if (referenceDate == null || referenceDate.trim().length === 0) {
    return resolvedTitle
  }

  const label = interventionDate != null ? 'Intervention' : 'Forecast cutoff'
  return `${resolvedTitle} · ${label}: ${formatCompactDateLabel(referenceDate)}`
}

/** Maps a scatter Copilot chart onto ChartCard scatter props (injects a series when none exists). */
const mapScatterChartToChartCardProps = (chart: CopilotChartData): ChartCardProps<string, ChartDataRow<string>> => {
  const { xField, yField, sourceCallId } = chart
  const effectiveColorField = getEffectiveColorField(chart)
  const injectScatterSeries = effectiveColorField.length === 0
  const thirdAxisField = injectScatterSeries ? COPILOT_SCATTER_SERIES_FIELD : effectiveColorField
  const normalizedData = normalizeAgentChartRows(chart.data, xField, yField, {
    chartType: 'scatter',
    colorField: effectiveColorField.length > 0 ? effectiveColorField : undefined,
    injectScatterSeries
  })

  const dimensions = [
    ...(injectScatterSeries
      ? [
          {
            id: COPILOT_SCATTER_SERIES_FIELD,
            label: 'Series',
            type: 'string' as const,
            descriptions: {
              [COPILOT_SCATTER_SERIES_VALUE]: {
                label: COPILOT_SCATTER_SERIES_VALUE
              }
            }
          }
        ]
      : []),
    ...(effectiveColorField.length > 0 ? [buildAgentDimension(effectiveColorField, chart.data)] : [])
  ]

  const chartDefinition: ChartCardScatterChartProps<string, ChartDataRow<string>> = {
    type: 'scatter',
    firstAxis: xField,
    secondAxis: yField,
    thirdAxis: thirdAxisField
  }

  return {
    card: {
      id: sourceCallId,
      title: buildChartTitle(chart, effectiveColorField),
      type: 'chart',
      chart: chartDefinition
    },
    dataDefinition: {
      dimensions,
      metrics: [buildAgentMetric(xField, chart.data), buildAgentMetric(yField, chart.data)]
    },
    data: {
      data: normalizedData,
      isLoading: false,
      isError: false
    },
    mutable: {
      selections: {}
    },
    customisations: sharedCustomisations
  }
}

/**
 * Maps bar/line/area/forecast charts onto ChartCard cartesian props.
 * Multi-series charts transpose the metric across the color/series field.
 */
const mapCartesianChartToChartCardProps = (chart: CopilotChartData): ChartCardProps<string, ChartDataRow<string>> => {
  const { xField, yField, sourceCallId } = chart
  const effectiveColorField = getEffectiveColorField(chart)
  const hasColorSeries = effectiveColorField.length > 0
  const cartesianChartType = resolveCartesianChartType(chart.chartType)
  const normalizedData = normalizeAgentChartRows(chart.data, xField, yField, {
    chartType: chart.chartType === 'forecast' ? 'line' : chart.chartType,
    colorField: hasColorSeries ? effectiveColorField : undefined
  })

  const dimensions = [buildAgentDimension(xField, chart.data)]
  if (hasColorSeries) {
    dimensions.push(
      effectiveColorField === COPILOT_FORECAST_SERIES_FIELD
        ? buildForecastSeriesDimension()
        : buildAgentDimension(effectiveColorField, chart.data)
    )
  }

  const chartDefinition: ChartCardCartesianChartProps<string, ChartDataRow<string>> = {
    type: cartesianChartType,
    firstAxis: buildFirstAxis(chart, normalizedData, hasColorSeries),
    secondAxis: hasColorSeries
      ? {
          id: 'copilot-metric',
          type: 'metricTransposed',
          change: {
            change: 'internal',
            options: [yField]
          },
          transpose: effectiveColorField
        }
      : yField
  }

  return {
    card: {
      id: sourceCallId,
      title: buildChartTitle(chart, effectiveColorField),
      type: 'chart',
      chart: chartDefinition
    },
    dataDefinition: {
      dimensions,
      metrics: [buildAgentMetric(yField, chart.data)]
    },
    data: {
      data: normalizedData,
      isLoading: false,
      isError: false
    },
    mutable: {
      selections: {}
    },
    customisations: sharedCustomisations
  }
}

/**
 * Returns ChartCard props for a Copilot chart, or null when the chart cannot be mapped
 * (missing axes or unsupported type). Callers typically show “Chart unavailable”.
 */
export const mapCopilotChartToChartCardProps = (
  chart: CopilotChartData
): ChartCardProps<string, ChartDataRow<string>> | null => {
  if (!canMapCopilotChartToChartCard(chart)) {
    return null
  }

  if (chart.chartType === 'scatter') {
    return mapScatterChartToChartCardProps(chart)
  }

  return mapCartesianChartToChartCardProps(chart)
}
